import _ from 'lodash';
import Builder from 'react-native-jala-orm/src/model/Builder';
import QueryBuilder from 'react-native-jala-orm/src/query/Builder';
import Expression from 'react-native-jala-orm/src/query/Expression';
import SyncableModel from './SyncableModel';
import Pond from './Pond';
import Farm from './Farm';
import { getRootStore } from "../utils/root-store"
import Batch from "./Batch"

export default class Cycle extends SyncableModel {
    static name = 'Cycle'

    _fillable = [
      'id', 'pond_id', 'species_id',
      'total_seed', 'hatchery_id',
      'started_at', 'finished_at',
      'area', 'initial_age', 'limit_weight_per_area', 'target_cultivation_day',
      'target_size', 'target_survival_rate', 'preparation_day',
      'remark', 'state', 'pro', 'deep',
      'created_at', 'updated_at',
      'feed_price', 'target_fcr', 'survival_rate_prediction_method', 'feeding_tray_count', 'total_seed_type',
      'synced_at', 'deleted_at',
    ]

    _casts = {
      pro: 'boolean',
      state: 'object',
    }

    static _parents = ['pond'];

    static endpoint = '/cycles'

    static searchables = ['pond_name']

    static _boot() {
      super._boot();

      this.addGlobalScope('global', (query: Builder) => {
        if (_.isEmpty(query.getQuery().columns)) {
          query.select([query.qualifyColumn('*')]);
        }

        const pond = (new Pond()).setTable(`${query.getModel().getTable()}_ponds`);
        query.leftJoin(
          `${(new Pond()).getTable()} as ${pond.getTable()}`,
          pond.getQualifiedKeyName(), query.qualifyColumn(pond.getForeignKey())
        );
        const farm = (new Farm()).setTable(`${query.getModel().getTable()}_farms`);
        query.leftJoin(
          `${(new Farm()).getTable()} as ${farm.getTable()}`,
          farm.getQualifiedKeyName(), pond.qualifyColumn(farm.getForeignKey())
        );
      });
    }

    qualifyColumn(column: string): string {
      if (column === '*') {
        return new Expression(
          `${this.getTable()}.*, `
          + `${this.qualifyColumn('pond_name')} as pond_name, `
          + `${this.qualifyColumn('pond_name_alpha_numeric')} as pond_name_alpha_numeric, `
          + `${this.qualifyColumn('age')} as age, `
          + `${this.qualifyColumn('timezone')} as timezone`
        );
      }
      if (column === 'age') {
        return new Expression(
          'MIN('
            + 'CAST(('
              + `JULIANDAY(COALESCE(${this.qualifyColumn('finished_at')}, DATE('now', ${this.qualifyColumn('timezone')}))) - `
              + `JULIANDAY(${this.qualifyColumn('started_at')})) AS INTEGER`
            + `) + COALESCE(${this.qualifyColumn('initial_age')}, 0),`
            + `COALESCE(${this.qualifyColumn('target_cultivation_day')}, 120)`
          + ')'
        );
      }
      if (column === 'timezone') {
        const farm = (new Farm()).setTable(`${this.getTable()}_farms`);
        return new Expression(`COALESCE(${farm.qualifyColumn('timezone')}, '+07:00')`);
      }
      if (column === 'batch_margin') {
        const farm = (new Farm()).setTable(`${this.getTable()}_farms`);
        return new Expression(`COALESCE(${farm.qualifyColumn('batch_margin')}, 45)`);
      }
      if (column === 'farm_id') {
        const pond = (new Pond()).setTable(`${this.getTable()}_ponds`);
        return pond.qualifyColumn('farm_id');
      }
      if (column === 'preparation_started_date') {
        return new Expression(`date(${this.qualifyColumn('started_at')}, ('-' || COALESCE(${this.qualifyColumn('preparation_day')}, 21) + ${this.qualifyColumn('initial_age')} || ' day'))`);
      }
      if (column.startsWith('pond_') && column !== 'pond_id') {
        const pond = (new Pond()).setTable(`${this.getTable()}_ponds`);
        return pond.qualifyColumn(column.replace('pond_', ''));
      }
      if (column === 'start_batch') {
        const batch = new Batch().setTable(`${this.getTable()}_batches`);
        return batch.qualifyColumn('start_batch');
      }
      if (column === 'farm_id_start_batch') {
        return new Expression(`(${this.qualifyColumn('farm_id')} || '-' || ${this.qualifyColumn('start_batch')})`);
      }
      return super.qualifyColumn(column);
    }

    scopeLatestByPond(query: Builder, pond: Pond = null) {
      query.withGlobalScope(_.uniqueId('t'), (q: Builder) => {
        const cycle = (new Cycle()).setTable(_.uniqueId('t'));
        return q.leftJoin(`${(new Cycle()).getTable()} as ${cycle.getTable()}`, (joinQuery: QueryBuilder) => {
          joinQuery.whereColumn(cycle.qualifyColumn('pond_id'), q.qualifyColumn('pond_id'))
            .whereColumn(cycle.qualifyColumn('started_at'), '>', q.qualifyColumn('started_at'));
        }).whereNull(cycle.getQualifiedKeyName())
          .when(pond && pond.getKey(), (queryPond: Builder) => {
            queryPond.where(queryPond.qualifyColumn('pond_id'), pond.getKey());
          });
      });
      return query
    }

    scopeUnfinished(query: Builder) {
      query.whereNull(query.qualifyColumn('finished_at'));
    }

    relationPond() {
      return Pond.query().withoutGlobalScope('soft_delete').whereKey(this.pond_id);
    }

    static latestByPond(pond: Pond = null) {
      return (new Cycle()).scopeLatestByPond(this.query(), pond)
    }

    static async syncAll(
      params = {}, sqlParams = [], cleanAfterSync = true,
      onSyncProgressUpdate = undefined, onError = undefined
    ) {
      const { userStore: { user } } = getRootStore();
      const _params = { ...params };
      const _sqlParams = [...sqlParams];
      if (user && user.role_id === 1 && !_params.pond_id__in) {
        _params.has = 'pond.farm.farm_user';
      }
      return super.syncAll(_params, _sqlParams, cleanAfterSync, onSyncProgressUpdate, onError);
    }
}
